
import { Box, Button, CircularProgress, Drawer, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { NewsFeedItem, useGetNewsFeedByIdQuery, useGetNewsFeedQuery, usePrefetch } from '../../../services/grace'
import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAlertDispatch } from '../../../context/AlertContext/AlertContext';

export default function NewsFeedGrid(): JSX.Element {
     
    const [newsId, setNewsId] = useState<string | null>(null);
    const [page, setPage] = useState(1);

    // State variable to track if data is empty
    const [isNewsFeedDataEmpty, setIsNewsFeedDataEmpty] = useState(false);

    const { data: newsFeedData, isError, isFetching } = useGetNewsFeedQuery(page);
    const prefetchPage = usePrefetch('getNewsFeed');

    const prefetchNext = useCallback(() => {
        prefetchPage(page + 1)
    }, [prefetchPage, page]);
    
    // Check if data is empty when it changes
    useEffect(() => {
        if (newsFeedData && newsFeedData.length === 0) {
            setIsNewsFeedDataEmpty(true);
        } else {
            setIsNewsFeedDataEmpty(false);
        }
    }, [newsFeedData]);

    const openDrawer = (newsItem: NewsFeedItem) => {
        setNewsId(newsItem.id.toString());
    };
    const closeDrawer = () => {
        setNewsId(null)
    };
  
    if (isError) {
        return <div>Error loading news</div>;
    }
    
    if (isFetching) {
        return (
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                padding: 1,
                margin: 1,
                width: 350,
                height: 200,
                justifyContent: "center",
                alignItems: "center"}}
            >
                <Typography sx={{padding: 1}}>Loading</Typography>
                <CircularProgress/>
            </Box>
        )
    }

    return (
        <> 
            {newsFeedData && (
                <Grid container spacing={4}>
                {newsFeedData.length > 0 && newsFeedData.map((item) => (
                    <Grid item key={item.id} xs={12} md={3}>
                        <Paper onClick={() => openDrawer(item)} elevation={3} style={{  background: '#F5F5F5', display: 'flex', alignItems: 'center', cursor: 'pointer' }} >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={12}>
                                    <img src={item.image} alt={item.title} style={{ maxWidth: '100%', marginRight: '16px', height: '100%', minHeight: '273px', objectFit: 'cover' }} />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <div style={{ padding: '0 16px' }}>
                                        <Typography variant="h6" sx={{ minHeight: '100px', WebkitLineClamp: '3', WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box' }}>{item.title}</Typography>
                                        <Typography variant="body1" sx={{textAlign: 'right', marginBottom: '20px'}}>
                                            <FontAwesomeIcon icon={['fas', 'up-right-from-square']} />
                                        </Typography>
                                    </div>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                ))}
                {newsFeedData.length === 0 && (
                    <Grid item xs={12} md={9}>No News</Grid>
                )}
                </Grid> 
            )}

            <Stack spacing={2} mt={2} direction="row" alignItems="center">
                <Button 
                    onClick={() => setPage(page - 1)} 
                    disabled={page === 1} 
                    variant="contained" 
                    endIcon={<FontAwesomeIcon icon={['far', 'chevron-left']} />}
                >
                    Previous
                </Button>
                <Button 
                    onClick={() => setPage(page + 1)} 
                    disabled={isNewsFeedDataEmpty} // Disable if data is empty
                    onMouseEnter={prefetchNext} 
                    variant="contained" 
                    endIcon={<FontAwesomeIcon icon={['far', 'chevron-right']} />}
                >
                    Next
                </Button>
                <div>Page {page}</div>
            </Stack>
            {newsId && 
                <NewsFeedArticleDrawer id={newsId} onClose={closeDrawer} />
            }
        </>
    )
}

const NewsFeedArticleDrawer = ({ id, onClose }: { id: string; onClose: () => void }) => {

    const alertDispatch = useAlertDispatch();

    const {
        data: newsFeedItem,
        error,
        isFetching,
        isError,
    } = useGetNewsFeedByIdQuery(id || '', {skip: !id});

    useEffect(() => {
        if (isError) {
            const err = error as FetchBaseQueryError;
            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        open: true,
                        type: "general",
                        message: "Error retrieving news feed data - please try again",
                    }
                });
            }
        }
    }, [id, newsFeedItem, isFetching, isError, error, alertDispatch]);
 
    return (
        <Drawer anchor="right" open={id !== null} onClose={onClose} 
            ModalProps={{
                keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
                display: { xs: 'block', sm: 'block' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 'calc(100vw - 50%)' },
            }}
        >
            <IconButton onClick={onClose} style={{ position: 'absolute', right: '8px', top: '8px' }}>
                <FontAwesomeIcon icon={['far', 'xmark']} color="green" title="Close" />
            </IconButton>
            {isFetching && (
                <Box sx={{
                    display: "flex",
                    flexDirection: "column", 
                    width: '100%',
                    height: '100%',
                    justifyContent: "center",
                    alignItems: "center"}}
                >
                    <Typography sx={{padding: 1}}>Loading</Typography>
                    <CircularProgress/>
                </Box>
            )}
            {!isFetching && (
                <div style={{ padding: '16px' }}>
                    {newsFeedItem && (
                        <>
                            <Typography variant="h6">{newsFeedItem.title}</Typography>
                            <Typography variant="caption">{newsFeedItem.pubDate}</Typography>
                            <img src={newsFeedItem.image} alt={newsFeedItem.title} style={{ maxWidth: '100%' }} />
                            {newsFeedItem.content && 
                                <div className='news-feed-content' dangerouslySetInnerHTML={{ __html: newsFeedItem.content }}></div>
                            } 
                        </>
                    )}
                    {!newsFeedItem && (
                        <Box sx={{
                            display: "flex",
                            flexDirection: "column", 
                            width: '100%',
                            height: '100vh',
                            justifyContent: "center",
                            alignItems: "center"}}
                        >
                            <Typography sx={{padding: 1}}>Couldn't load the news..</Typography>
                        </Box>
                    )}
                </div>
            )}
            
        </Drawer>
    )
}